import type { ReactNode } from "react";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { XIcon, SearchIcon, UserGroupIcon, InformationCircleIcon } from "@heroicons/react/outline";
import type { AvailableLearner, SmartGroupCriteria, LearnerGroup } from "../types/groupTypes";
import { getEntityColor } from "../utils/comparisonColors";

interface SmartCriteriaOptionProps {
  checked: boolean;
  onSelect: () => void;
  titleKey: string;
  tooltipKey: string;
  children?: ReactNode;
}

const SmartCriteriaOption = ({
  checked,
  onSelect,
  titleKey,
  tooltipKey,
  children,
}: SmartCriteriaOptionProps) => {
  const { t } = useTranslation();

  return (
    <label className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
      <input
        type="radio"
        name="smartCriteria"
        checked={checked}
        onChange={onSelect}
        className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500"
      />
      <div className="ml-3 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-gray-900">{t(titleKey)}</p>
          <div className="group relative">
            <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-[100] invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-56 shadow-lg whitespace-normal">
                {t(tooltipKey)}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
              </div>
            </div>
          </div>
        </div>
        {children}
      </div>
    </label>
  );
};

interface CreateLearnerGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateGroup: (name: string, memberIds: (number | string)[]) => boolean;
  onCreateSmartGroup: (
    name: string,
    criteria: SmartGroupCriteria,
    learners: AvailableLearner[],
  ) => boolean;
  availableLearners: AvailableLearner[];
  availableParcours: { id: number; name: string }[];
  existingGroups: LearnerGroup[];
}

type TabType = "manual" | "smart";

const CreateLearnerGroupModal = ({
  isOpen,
  onClose,
  onCreateGroup,
  onCreateSmartGroup,
  availableLearners,
  availableParcours,
  existingGroups,
}: CreateLearnerGroupModalProps) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<TabType>("manual");
  const [groupName, setGroupName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedLearnerIds, setSelectedLearnerIds] = useState<Set<number | string>>(new Set());

  const [smartCriteriaType, setSmartCriteriaType] =
    useState<SmartGroupCriteria["type"]>("top_performers");
  const [topPercentage, setTopPercentage] = useState(20);
  const [completionThreshold, setCompletionThreshold] = useState(50);
  const [selectedParcoursId, setSelectedParcoursId] = useState<number | null>(null);

  const filteredLearners = useMemo(() => {
    if (!searchValue) return availableLearners;
    const search = searchValue.toLowerCase();
    return availableLearners.filter((l) => l.name.toLowerCase().includes(search));
  }, [availableLearners, searchValue]);

  const getLearnerGroups = (learnerId: number | string) => {
    const results: { group: LearnerGroup; index: number }[] = [];
    for (let i = 0; i < existingGroups.length; i++) {
      if (existingGroups[i].memberIds.includes(learnerId)) {
        results.push({ group: existingGroups[i], index: i });
      }
    }
    return results;
  };

  const smartGroupPreviews = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const withScores = availableLearners.filter((l) => l.averageScore !== null);

    return {
      top_performers: Math.ceil(withScores.length * (topPercentage / 100)),
      struggling: availableLearners.filter((l) => l.completionRate < completionThreshold).length,
      active_this_month: availableLearners.filter((l) => {
        if (!l.lastSessionDate) return false;
        const sessionDate =
          l.lastSessionDate instanceof Date ? l.lastSessionDate : new Date(l.lastSessionDate);
        return sessionDate >= startOfMonth;
      }).length,
      by_parcours: selectedParcoursId ? "?" : 0,
    };
  }, [availableLearners, topPercentage, completionThreshold, selectedParcoursId]);

  const handleToggleLearner = (learnerId: number | string) => {
    const newSet = new Set(selectedLearnerIds);
    if (newSet.has(learnerId)) {
      newSet.delete(learnerId);
    } else {
      newSet.add(learnerId);
    }
    setSelectedLearnerIds(newSet);
  };

  const handleSelectAll = () => {
    if (selectedLearnerIds.size === filteredLearners.length) {
      setSelectedLearnerIds(new Set());
    } else {
      setSelectedLearnerIds(new Set(filteredLearners.map((l) => l.id)));
    }
  };

  const handleCreate = () => {
    if (!groupName.trim()) return;

    let success: boolean;

    if (activeTab === "manual") {
      if (selectedLearnerIds.size === 0) return;
      success = onCreateGroup(groupName.trim(), Array.from(selectedLearnerIds));
    } else {
      let criteria: SmartGroupCriteria;
      switch (smartCriteriaType) {
        case "top_performers":
          criteria = { type: "top_performers", percentage: topPercentage };
          break;
        case "struggling":
          criteria = { type: "struggling", completionThreshold };
          break;
        case "active_this_month":
          criteria = { type: "active_this_month" };
          break;
        case "by_parcours":
          if (!selectedParcoursId) return;
          criteria = { type: "by_parcours", parcoursId: selectedParcoursId };
          break;
        default:
          return;
      }
      success = onCreateSmartGroup(groupName.trim(), criteria, availableLearners);
    }

    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setGroupName("");
    setSearchValue("");
    setSelectedLearnerIds(new Set());
    setActiveTab("manual");
    setSmartCriteriaType("top_performers");
    setTopPercentage(20);
    setCompletionThreshold(50);
    setSelectedParcoursId(null);
    onClose();
  };

  const isCreateDisabled = useMemo(() => {
    if (!groupName.trim()) return true;
    if (activeTab === "manual") {
      return selectedLearnerIds.size === 0;
    }
    if (smartCriteriaType === "by_parcours" && !selectedParcoursId) {
      return true;
    }
    const previewCount = smartGroupPreviews[smartCriteriaType];
    if (typeof previewCount === "number" && previewCount === 0) {
      return true;
    }
    return false;
  }, [
    groupName,
    activeTab,
    selectedLearnerIds.size,
    smartCriteriaType,
    selectedParcoursId,
    smartGroupPreviews,
  ]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t("statsNew.comparateur.groups.createGroup")}
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("statsNew.comparateur.groups.groupName")}
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t("statsNew.comparateur.groups.groupNamePlaceholder")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div className="flex border-b mb-4">
            <button
              onClick={() => setActiveTab("manual")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "manual"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t("statsNew.comparateur.groups.manualSelection")}
            </button>
            <button
              onClick={() => setActiveTab("smart")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "smart"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t("statsNew.comparateur.groups.smartGrouping")}
            </button>
          </div>

          {activeTab === "manual" ? (
            <div className="h-[340px]">
              <div className="relative mb-3">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t("statsNew.comparateur.groups.searchLearners")}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">
                  {selectedLearnerIds.size} {t("statsNew.comparateur.groups.selected")}
                </span>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  {selectedLearnerIds.size === filteredLearners.length
                    ? t("statsNew.comparateur.groups.deselectAll")
                    : t("statsNew.comparateur.groups.selectAll")}
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg h-64 overflow-y-auto">
                {filteredLearners.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {t("statsNew.comparateur.groups.noLearnersFound")}
                  </div>
                ) : (
                  filteredLearners.map((learner) => {
                    const groupsInfo = getLearnerGroups(learner.id);
                    return (
                      <label
                        key={learner.id}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLearnerIds.has(learner.id)}
                          onChange={() => handleToggleLearner(learner.id)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {learner.name}
                            </p>
                            {groupsInfo.map((info) => (
                              <span
                                key={info.group.id}
                                className="text-[10px] px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
                                style={{ backgroundColor: getEntityColor(info.index).primary }}
                              >
                                {info.group.name}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">{learner.totalSessions} sessions</p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <div className="h-[340px] space-y-4 overflow-visible">
              <div className="space-y-3">
                <SmartCriteriaOption
                  checked={smartCriteriaType === "top_performers"}
                  onSelect={() => setSmartCriteriaType("top_performers")}
                  titleKey="statsNew.comparateur.groups.topPerformers"
                  tooltipKey="statsNew.comparateur.groups.topPerformersTooltip"
                >
                  {smartCriteriaType === "top_performers" && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        value={topPercentage}
                        onChange={(e) =>
                          setTopPercentage(
                            Math.min(100, Math.max(1, parseInt(e.target.value) || 1)),
                          )
                        }
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                        min="1"
                        max="100"
                      />
                      <span className="text-sm text-gray-500">%</span>
                      <span className="text-xs text-gray-400">
                        (~{smartGroupPreviews.top_performers}{" "}
                        {t("statsNew.comparateur.groups.learners")})
                      </span>
                    </div>
                  )}
                </SmartCriteriaOption>

                <SmartCriteriaOption
                  checked={smartCriteriaType === "struggling"}
                  onSelect={() => setSmartCriteriaType("struggling")}
                  titleKey="statsNew.comparateur.groups.struggling"
                  tooltipKey="statsNew.comparateur.groups.strugglingTooltip"
                >
                  {smartCriteriaType === "struggling" && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-gray-500">&lt;</span>
                      <input
                        type="number"
                        value={completionThreshold}
                        onChange={(e) =>
                          setCompletionThreshold(
                            Math.min(100, Math.max(1, parseInt(e.target.value) || 1)),
                          )
                        }
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                        min="1"
                        max="100"
                      />
                      <span className="text-sm text-gray-500">%</span>
                      <span className="text-xs text-gray-400">
                        (~{smartGroupPreviews.struggling}{" "}
                        {t("statsNew.comparateur.groups.learners")})
                      </span>
                    </div>
                  )}
                </SmartCriteriaOption>

                <SmartCriteriaOption
                  checked={smartCriteriaType === "active_this_month"}
                  onSelect={() => setSmartCriteriaType("active_this_month")}
                  titleKey="statsNew.comparateur.groups.activeThisMonth"
                  tooltipKey="statsNew.comparateur.groups.activeThisMonthTooltip"
                >
                  <p className="text-xs text-gray-400">
                    ~{smartGroupPreviews.active_this_month}{" "}
                    {t("statsNew.comparateur.groups.learners")}
                  </p>
                </SmartCriteriaOption>

                {availableParcours.length > 0 && (
                  <SmartCriteriaOption
                    checked={smartCriteriaType === "by_parcours"}
                    onSelect={() => setSmartCriteriaType("by_parcours")}
                    titleKey="statsNew.comparateur.groups.byParcours"
                    tooltipKey="statsNew.comparateur.groups.byParcoursTooltip"
                  >
                    {smartCriteriaType === "by_parcours" && (
                      <select
                        value={selectedParcoursId ?? ""}
                        onChange={(e) =>
                          setSelectedParcoursId(e.target.value ? parseInt(e.target.value) : null)
                        }
                        className="mt-2 w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">{t("statsNew.comparateur.groups.selectParcours")}</option>
                        {availableParcours.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </SmartCriteriaOption>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {t("general.cancel")}
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreateDisabled}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              isCreateDisabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {t("statsNew.comparateur.groups.create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLearnerGroupModal;
